/**
 * The Library's search: typo-tolerant, word by word. Every word typed has to
 * match a word in the row (its name, brand or store), either as the start of
 * that word or within a few typos of it, so "luna rosa carbn" finds Luna Rossa
 * Carbon and "kurkdjan" finds Maison Francis Kurkdjian, while "dior" finds
 * Dior and not "Acqua di Giò". Short words (three letters or fewer) must
 * match exactly, as the start of a word: one typo in "do" could be anything.
 * A typo'd word must still start with the right letter.
 */

/** Below this many letters a search filters nothing: one letter matches too much. */
export const MIN_SEARCH_LENGTH = 2

/** "Acqua di Giò" -> "acqua di gio", "Dolce & Gabbana" -> "dolce gabbana". */
export const normalizeSearch = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()

const words = (text: string) => normalizeSearch(text).split(' ').filter(Boolean)

/** True when there is enough typed to search. */
export const isSearching = (query: string) =>
  normalizeSearch(query).replace(/ /g, '').length >= MIN_SEARCH_LENGTH

/** Typos a word of this length may have: 0 up to 3 letters, 1 up to 6, then 2. */
const allowedTypos = (length: number) => (length <= 3 ? 0 : length <= 6 ? 1 : 2)

const cell = (row: number[], j: number) => row[j] ?? Infinity

/**
 * Edits between two words (insert, delete, swap a letter, or swap two
 * neighbours), giving up once past `max`.
 */
const typos = (a: string, b: string, max: number) => {
  if (Math.abs(a.length - b.length) > max) return max + 1
  let prevPrev: number[] = []
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const row = [i]
    let rowMin = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let value = Math.min(
        cell(prev, j) + 1,
        cell(row, j - 1) + 1,
        cell(prev, j - 1) + cost
      )
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, cell(prevPrev, j - 2) + 1)
      }
      row.push(value)
      rowMin = Math.min(rowMin, value)
    }
    if (rowMin > max) return max + 1
    prevPrev = prev
    prev = row
  }
  return cell(prev, b.length)
}

/**
 * How well one typed word matches one word of the row: 0 the same word, 0.5
 * the start of it, 1 per typo; null no match. A typo'd start counts too
 * ("kurkdja" for "kurkdjian"), as a shopper may still be typing.
 */
const wordScore = (typed: string, word: string): number | null => {
  if (word === typed) return 0
  if (word.startsWith(typed)) return 0.5
  const max = allowedTypos(typed.length)
  // A typo'd word still starts right: "dior" is not one letter from "Giorgio".
  if (max === 0 || word[0] !== typed[0]) return null
  const whole = typos(typed, word, max)
  if (whole <= max) return whole
  // The start of a longer word, a letter or two either side of the typed
  // length, so a skipped letter counts once ("kurdji" for "kurkdjian").
  let start = max + 1
  for (let n = typed.length - max; n <= typed.length + max; n++) {
    if (n >= 1 && n < word.length) {
      start = Math.min(start, typos(typed, word.slice(0, n), max))
    }
  }
  return start <= max ? start + 0.5 : null
}

/**
 * How well `query` matches `text`: lower is better; null when any typed word
 * matches nothing.
 */
export const searchScore = (query: string, text: string): number | null => {
  const typed = words(query)
  if (typed.length === 0) return 0
  const own = words(text)
  let total = 0
  for (const word of typed) {
    let best: number | null = null
    for (const candidate of own) {
      const score = wordScore(word, candidate)
      if (score !== null && (best === null || score < best)) best = score
      if (best === 0) break
    }
    if (best === null) return null
    total += best
  }
  return total
}

/**
 * The items matching `query`, best first (ties keep their order). Too short a
 * query returns them all, as they were.
 */
export const searchItems = <T>(
  items: T[],
  query: string,
  textOf: (item: T) => string
): T[] => {
  if (!isSearching(query)) return items
  return items
    .map((item, index) => ({
      item,
      index,
      score: searchScore(query, textOf(item)),
    }))
    .filter(
      (hit): hit is { item: T; index: number; score: number } =>
        hit.score !== null
    )
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((hit) => hit.item)
}
